import { useCallback, useEffect, useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { AlertCircle, ArrowRight, Check, Loader2, LockKeyhole, ShieldCheck } from 'lucide-react'

import { AuthLegalNotice, AuthPageShell } from '@/components/auth/auth-page-shell'
import { LoginForm } from '@/components/auth/login-form'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { useAuth } from '@/hooks/useAuth'
import { oauthSignInCredentials, type OAuthProvider } from '@/lib/oauth-providers'
import { supabase } from '@/lib/supabase'

type ConsentSearch = {
  authorization_id?: string
}

type AuthorizationDetails = NonNullable<
  Awaited<ReturnType<typeof supabase.auth.oauth.getAuthorizationDetails>>['data']
>

/**
 * auth-js's declared `getAuthorizationDetails` type only models GoTrue's
 * pending-consent shape and applies zero runtime validation, but GoTrue's
 * `GET /oauth/authorizations/{id}` handler actually returns one of two
 * mutually exclusive bodies. When the user has already consented, GoTrue
 * responds with just `{ redirect_url }` (a different struct, `ConsentResponse`)
 * and has already minted the authorization code server-side. This type models
 * that second shape honestly instead of trusting the SDK's type.
 */
type AlreadyConsentedAuthorization = {
  redirect_url: string
}

type OAuthConsentContentProps = {
  authorizationId?: string
  redirect?: (url: string) => void
}

function redirectBrowser(url: string) {
  window.location.assign(url)
}

function clientDisplayName(client: AuthorizationDetails['client']): string {
  if (client.name) return client.name
  if (client.uri) {
    try {
      return new URL(client.uri).hostname
    } catch {
      return client.uri
    }
  }
  return 'this application'
}

export const Route = createFileRoute('/oauth/consent')({
  validateSearch: (search: Record<string, unknown>): ConsentSearch => ({
    authorization_id:
      typeof search.authorization_id === 'string' ? search.authorization_id : undefined,
  }),
  component: OAuthConsentPage,
})

export function OAuthConsentPage() {
  const { authorization_id: authorizationId } = Route.useSearch()
  return <OAuthConsentContent authorizationId={authorizationId} />
}

export function OAuthConsentContent({
  authorizationId,
  redirect = redirectBrowser,
}: OAuthConsentContentProps) {
  const { session, isLoading: authLoading } = useAuth()
  const [details, setDetails] = useState<AuthorizationDetails | null>(null)
  const [loadingDetails, setLoadingDetails] = useState(false)
  const [submitting, setSubmitting] = useState<'approve' | 'deny' | null>(null)
  const [signingIn, setSigningIn] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const loadAuthorization = useCallback(async () => {
    if (!authorizationId || !session) return
    setLoadingDetails(true)
    setError(null)
    const { data, error: detailsError } = await supabase.auth.oauth.getAuthorizationDetails(authorizationId)
    if (detailsError || !data) {
      setError(detailsError?.message || 'This authorization request is no longer valid.')
      setLoadingDetails(false)
      return
    }
    // `authorization_id` is the real discriminator between GoTrue's two response
    // shapes (see AlreadyConsentedAuthorization above). Its absence means the
    // user already consented and GoTrue already minted the authorization code
    // server-side, so redirect rather than attempting to approve again.
    if (!('authorization_id' in data)) {
      const consented = data as unknown as AlreadyConsentedAuthorization
      if (consented.redirect_url) {
        redirect(consented.redirect_url)
        return
      }
      setError('This authorization request is no longer valid.')
      setLoadingDetails(false)
      return
    }
    if (!data.client) {
      setError('This authorization request is no longer valid.')
      setLoadingDetails(false)
      return
    }
    setDetails(data)
    setLoadingDetails(false)
  }, [authorizationId, session, redirect])

  useEffect(() => {
    void loadAuthorization()
  }, [loadAuthorization])

  const signInWithPassword = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setSigningIn(true)
    setError(null)
    const form = new FormData(event.currentTarget)
    const { error: signInError } = await supabase.auth.signInWithPassword({
      email: String(form.get('email') || ''),
      password: String(form.get('password') || ''),
    })
    if (signInError) setError(signInError.message)
    setSigningIn(false)
  }

  const signInWithOAuthProvider = async (provider: OAuthProvider) => {
    if (!authorizationId) return
    setSigningIn(true)
    setError(null)
    const redirectTo = new URL('/oauth/consent', window.location.origin)
    redirectTo.searchParams.set('authorization_id', authorizationId)
    const { error: signInError } = await supabase.auth.signInWithOAuth(
      oauthSignInCredentials(provider, redirectTo.toString()),
    )
    if (signInError) {
      setError(signInError.message)
      setSigningIn(false)
    }
  }

  const decide = async (decision: 'approve' | 'deny') => {
    if (!authorizationId) return
    setSubmitting(decision)
    setError(null)
    const action = decision === 'approve'
      ? supabase.auth.oauth.approveAuthorization
      : supabase.auth.oauth.denyAuthorization
    const { data, error: decisionError } = await action(authorizationId, { skipBrowserRedirect: true })
    if (decisionError || !data?.redirect_url) {
      setError(decisionError?.message || 'Cortado could not complete this authorization request.')
      setSubmitting(null)
      return
    }
    redirect(data.redirect_url)
  }

  if (!authorizationId) {
    return <ConsentShell><Card><ConsentError message="This authorization link is missing its request ID." /></Card></ConsentShell>
  }

  if (authLoading || (session && loadingDetails)) {
    return (
      <ConsentShell>
        <Card>
          <CardContent className="flex items-center justify-center gap-3 py-14 text-sm text-muted-foreground">
            <Loader2 className="size-5 animate-spin" aria-hidden="true" />
            Preparing secure authorization…
          </CardContent>
        </Card>
      </ConsentShell>
    )
  }

  if (!session) {
    return (
      <ConsentShell contentClassName="max-w-sm">
        <p className="mb-6 flex items-center justify-center gap-3 font-financial text-xs uppercase tracking-[0.2em] text-dim">
          <span className="inline-block size-1.5 rounded-full bg-brand" aria-hidden="true" />
          <span className="text-brand">[</span>
          Connect an AI app
          <span className="text-brand">]</span>
        </p>
        <LoginForm
          onSubmit={signInWithPassword}
          onOAuthClick={signInWithOAuthProvider}
          loading={signingIn}
          title="Sign in to connect Cortado"
          description="Use the Cortado account whose organization you want this AI app to read."
          submitLabel="Continue"
          loadingLabel="Signing in…"
          fieldIdPrefix="oauth-"
          showAccountLinks={false}
        />
        {error && <div className="mt-4"><InlineError message={error} /></div>}
        <AuthLegalNotice />
      </ConsentShell>
    )
  }

  if (error && !details) {
    return <ConsentShell><Card><ConsentError message={error} /></Card></ConsentShell>
  }

  if (!details) {
    return <ConsentShell><Card><ConsentError message="This authorization request is unavailable." /></Card></ConsentShell>
  }

  const scopes = (details.scope || '').split(/\s+/).filter(Boolean)
  const clientLabel = clientDisplayName(details.client)
  const userEmail = details.user?.email
  return (
    <ConsentShell>
      <Card>
        <CardHeader className="text-center">
          <div className="mx-auto mb-2 flex size-11 items-center justify-center rounded-full border border-border bg-elevated">
            <ShieldCheck className="size-5 text-brand" aria-hidden="true" />
          </div>
          <CardTitle>Connect {clientLabel} to Cortado?</CardTitle>
          <CardDescription>
            {userEmail && `Signed in as ${userEmail}. `}
            You will return to {clientLabel} after choosing.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="space-y-3 rounded-lg border border-border bg-muted/30 p-4">
            <p id="mcp-access-heading" className="text-sm font-medium text-foreground">
              Your AI agent connected through this MCP will be able to:
            </p>
            <ul aria-labelledby="mcp-access-heading" className="space-y-3">
              <PermissionRow>Get and filter leads using the same filters as Cortado</PermissionRow>
              <PermissionRow>Get lead details, including attached inquiry events</PermissionRow>
              <PermissionRow>Get contacts associated with leads</PermissionRow>
            </ul>
          </div>
          <div className="flex gap-2 text-sm text-muted-foreground">
            <LockKeyhole className="mt-0.5 size-4 shrink-0 text-foreground" aria-hidden="true" />
            <p>
              This connection cannot create or change Cortado data. It is limited to the organization in
              the server URL and to your existing permissions.
            </p>
          </div>
          {scopes.length > 0 && (
            <p className="text-xs text-muted-foreground">Identity scopes requested: {scopes.join(', ')}</p>
          )}
          {error && <InlineError message={error} />}
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button type="button" variant="outline" className="min-h-11" onClick={() => decide('deny')} disabled={submitting !== null}>
              {submitting === 'deny' && <Loader2 className="size-4 animate-spin" aria-hidden="true" />}
              Decline
            </Button>
            <Button type="button" className="min-h-11" onClick={() => decide('approve')} disabled={submitting !== null}>
              {submitting === 'approve' ? <Loader2 className="size-4 animate-spin" aria-hidden="true" /> : <ArrowRight className="size-4" aria-hidden="true" />}
              Authorize
            </Button>
          </div>
        </CardContent>
      </Card>
    </ConsentShell>
  )
}

function ConsentShell({
  children,
  contentClassName = 'max-w-lg',
}: {
  children: React.ReactNode
  contentClassName?: string
}) {
  return (
    <AuthPageShell contentClassName={contentClassName}>{children}</AuthPageShell>
  )
}

function PermissionRow({ children }: { children: React.ReactNode }) {
  return (
    <li className="flex gap-2 text-sm">
      <Check className="mt-0.5 size-4 shrink-0 text-brand" aria-hidden="true" />
      <span>{children}</span>
    </li>
  )
}

function InlineError({ message }: { message: string }) {
  return (
    <div role="alert" className="flex gap-2 rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
      <AlertCircle className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
      <span>{message}</span>
    </div>
  )
}

function ConsentError({ message }: { message: string }) {
  return (
    <CardContent className="space-y-4 py-10 text-center">
      <AlertCircle className="mx-auto size-8 text-destructive" aria-hidden="true" />
      <div>
        <h1 className="display-heading text-xl font-semibold">Unable to authorize</h1>
        <p className="mt-2 text-sm text-muted-foreground">{message}</p>
      </div>
    </CardContent>
  )
}
